import { FileTextOutlined } from '@ant-design/icons';
import {
  EnumInfoDelegaTipo,
  getGetFrontRichiesteIdModuliDelegheQueryKey,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { FormHandler, SubmitButton } from '@framework/form';
import { IModuleDelegation } from '@framework/frontoffice-submission';
import { getModuleTitle } from '@framework/frontoffice-submission/src/module/getModuleTitle';
import { useTranslation } from '@framework/i18n';
import {
  DividerTitleElement,
  Drawer,
  DrawerButtonRow,
  VerticalSpace,
} from '@framework/ui';
import { Button, Divider, List, Space, Typography, message } from 'antd';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { queryClient } from '../../../../core/network/queryClient';
import { useHandleModulesDelegationsSubmit } from '../hooks/useHandleModulesDelegationsSubmit';
import { useModuleDelegation } from '../hooks/useModuleDelegation';
import { DelegationTypeUserTag } from './DelegationTypeUserTag';
import { EditModulesDelegationsSchema } from './EditModulesDelegationsSchema';
import { ModulesDelegationInput } from './ModulesDelegationInput';
import { ModulesViewDelegationsInput } from './ModulesViewDelegationsInput';

const StyledDivider = styled(Divider)`
  &.ant-divider {
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;

const Text = Typography.Text;

export interface IEditModulesDelegationsDrawerProps {
  submissionId: string;
  modulesDelegations: IModuleDelegation[];
  allDelegations: IModuleDelegation[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function EditModulesDelegationsDrawer(
  props: IEditModulesDelegationsDrawerProps,
) {
  const { open, setOpen, modulesDelegations, submissionId, allDelegations } =
    props;

  const { t } = useTranslation();

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const { onSubmit } = useHandleModulesDelegationsSubmit({
    submissionId,
    allDelegations,
  });

  const {
    initialValues,
    compilation,
    sign,
    view,
    isDifferent,
    hasAtleastOneSignableModule,
  } = useModuleDelegation(modulesDelegations);

  return (
    <Drawer
      title={t(
        'delegation.modifica-deleghe-moduli-drawer.titolo',
        'Gestione Deleghe Moduli',
      )}
      open={open}
      onClose={handleClose}
      destroyOnClose
      size="large"
    >
      <FormHandler
        nested
        layout="vertical"
        schema={EditModulesDelegationsSchema}
        initialValues={initialValues}
        onSubmit={async values => {
          const moduleIds = modulesDelegations
            .map(m => m.submissionModule.id)
            .filter(Boolean) as string[];
          try {
            await onSubmit(values, moduleIds);
            await queryClient.invalidateQueries(
              getGetFrontRichiesteIdModuliDelegheQueryKey(submissionId),
            );
            message.success(
              t(
                'delegation.modifica-deleghe-moduli-drawer.deleghe-aggiornate',
                'Deleghe aggiornate con successo',
              ),
            );
            setOpen(false);
          } catch (e) {
            console.error(e);
            message.error(getNetworkErrorMessage(e, t("delegation.errore", 'Si è verificato un errore. Riprovare.'))); // prettier-ignore
          }
        }}
      >
        {/* Lista moduli selezionati */}
        <DividerTitleElement
          title={t(
            'delegation.modifica-deleghe-moduli-drawer.moduli-selezionati',
            'Moduli Selezionati',
          )}
          removeTopMargin
        />

        <List bordered>
          {modulesDelegations.map(moduleDelegation => (
            <List.Item key={moduleDelegation.submissionModule.id}>
              <Space>
                <FileTextOutlined aria-hidden />
                <Text strong>
                  {getModuleTitle(moduleDelegation.submissionModule)}
                </Text>
              </Space>
            </List.Item>
          ))}
        </List>

        {/* Gestione delegati */}
        <DividerTitleElement
          title={t(
            'delegation.modifica-deleghe-moduli-drawer.delegati',
            'Delegati',
          )}
        />

        <div>
          <ModulesDelegationInput
            icon={
              <DelegationTypeUserTag
                delegate={{
                  type: EnumInfoDelegaTipo.COMPILAZIONE,
                  userName: t(
                    'delegation.modifica-deleghe-moduli-drawer.delega-di-compilazione',
                    'Delega di Compilazione',
                  ),
                }}
              />
            }
            name="compilation"
            label={t(
              'delegation.modifica-deleghe-moduli-drawer.delega-di-compilazione',
              'Delega di Compilazione',
            )}
            currentValue={compilation}
            needConfirm={isDifferent.compilation}
          />
        </div>
        {hasAtleastOneSignableModule && (
          <>
            <StyledDivider />
            <div>
              <ModulesDelegationInput
                icon={
                  <DelegationTypeUserTag
                    delegate={{
                      type: EnumInfoDelegaTipo.FIRMA,
                      userName: t(
                        'delegation.modifica-deleghe-moduli-drawer.delega-di-firma',
                        'Delega di Firma',
                      ),
                    }}
                  />
                }
                name="sign"
                label={t(
                  'delegation.modifica-deleghe-moduli-drawer.delega-di-firma',
                  'Delega di Firma',
                )}
                currentValue={sign}
                needConfirm={isDifferent.sign}
                disabled={!hasAtleastOneSignableModule}
              />
            </div>
          </>
        )}
        <StyledDivider />
        <div>
          <ModulesViewDelegationsInput
            name="view"
            label={t(
              'delegation.modifica-deleghe-moduli-drawer.deleghe-di-visualizzazione',
              'Deleghe di Visualizzazione',
            )}
            uniqueDelegations={view}
            needConfirm={isDifferent.view}
          />
        </div>

        <VerticalSpace size={48} />

        <DrawerButtonRow>
          <Space>
            <Button size="large" onClick={handleClose}>
              {t('common.annulla', 'Annulla')}
            </Button>
            <SubmitButton />
          </Space>
        </DrawerButtonRow>
      </FormHandler>
    </Drawer>
  );
}
